import {useCallback} from "react";

import type {Result} from "@khanacademy/wonder-blocks-data";
import {useMutation} from "@khan/data-gql";

import type {DoGeneralAICompletion} from "../queries/__graphql-types__/DoGeneralAICompletion.ts";
import {DO_GENERAL_AI_COMPLETION} from "../queries/do-general-ai-completion.ts";

interface SendCompletionRequestFn {
    (
        prompt: string,
        temperature: number,
        maxTokens?: number | null | undefined,
        stopWord?: string | null | undefined,
    ): Promise<Result<DoGeneralAICompletion>>;
}

/**
 * Hook to return a function that sends a completion request to the AI service.
 *
 * #### Testing
 * When writing tests for hooks and components that use this hook, it is
 * generally safe to spy on this hook and return a mock function. However,
 * for a complete test that hooks into React in a real world manner, you should
 * mock the GQL response for the DO_GENERAL_AI_COMPLETION mutation. In many
 * cases, the latter may be easier as it is directly supported by our test
 * harnesses via the gql adapter. See the tests for this hook for an example
 * of creating a mock GQL response for the mutation.
 *
 * @returns A function that will perform the given completion request mutation.
 */
export const useSendCompletionRequest = (): SendCompletionRequestFn => {
    const [doGeneralAICompletion] = useMutation(DO_GENERAL_AI_COMPLETION);

    const sendCompletionRequest = useCallback(
        (prompt, temperature, maxTokens, stopWord) =>
            doGeneralAICompletion({
                prompt: prompt,
                temperature: temperature,
                maxTokens: maxTokens ?? 2048,
                stop: stopWord ? [stopWord] : [],
            }),
        [doGeneralAICompletion],
    );

    return sendCompletionRequest;
};
